import { useCallback, useMemo, useRef } from 'react';
import { UnknownSpec, useEvent } from '../../../core';
import {
  DefaultPluginKinds,
  Plugin,
  PluginImplementation,
  PluginLoader,
  PluginModuleResource,
  PluginType,
} from '../../model';
import { PluginRegistryContext } from '../../runtime';
import { getTypeAndKindKey, usePluginIndexes } from './plugin-indexes';

export interface PluginRegistryProps {
  pluginLoader: PluginLoader;
  defaultPluginKinds: DefaultPluginKinds;
  children?: React.ReactNode;
}

/**
 * PluginRegistryContext provider that keeps track of all available plugins and provides an API for getting them or
 * querying the metadata about them.
 */
export function PluginRegistry(props: PluginRegistryProps) {
  const {
    pluginLoader: { getInstalledPlugins, importPluginModule },
    children,
    defaultPluginKinds,
  } = props;

  const getPluginIndexes = usePluginIndexes(getInstalledPlugins);

  // De-dupe calls to import plugin modules
  const importCache = useRef(new Map<PluginModuleResource, Promise<unknown>>());

  // Do useEvent here since this accesses the importPluginModule prop and we want a stable reference to it for the
  // callback below
  const loadPluginModule = useEvent((resource: PluginModuleResource) => {
    let request = importCache.current.get(resource);
    if (request === undefined) {
      request = importPluginModule(resource);
      importCache.current.set(resource, request);

      // Remove failed requests from the cache so they can potentially be retried
      request.catch(() => importCache.current.delete(resource));
    }
    return request;
  });

  const getPlugin = useCallback(
    async <T extends PluginType>(pluginType: T, kind: string): Promise<PluginImplementation<T>> => {
      // Get the indexes of the installed plugins
      const pluginIndexes = await getPluginIndexes();

      // Figure out what module the plugin is in by looking in the index
      const typeAndKindKey = getTypeAndKindKey(pluginType, kind);
      const resource = pluginIndexes.pluginResourcesByTypeAndKind.get(typeAndKindKey);
      if (resource === undefined) {
        throw new Error(`A ${pluginType} plugin for kind '${kind}' is not installed`);
      }

      // Treat the plugin module as a bunch of named exports that have plugins
      const pluginModule = (await loadPluginModule(resource)) as Record<string, Plugin<UnknownSpec>>;

      // We currently assume that plugin modules will have named exports that match the kinds they handle
      let plugin: Plugin<UnknownSpec> | undefined = pluginModule[kind];

      // Check for new pattern with the dashboard variation of the component
      // nested in the `DashboardPanel` property, and use that instead when
      // available.
      if (plugin && pluginType === 'Panel' && 'DashboardPanel' in plugin && plugin.DashboardPanel) {
        plugin = plugin.DashboardPanel as Plugin<UnknownSpec>;
      }

      if (plugin === undefined) {
        throw new Error(
          `The ${pluginType} plugin for kind '${kind}' is missing from the ${resource.metadata.name} plugin module`
        );
      }

      return plugin as PluginImplementation<T>;
    },
    [getPluginIndexes, loadPluginModule]
  );

  const listPluginMetadata = useCallback(
    async (pluginType: PluginType) => {
      const pluginIndexes = await getPluginIndexes();
      return pluginIndexes.pluginMetadataByType.get(pluginType) ?? [];
    },
    [getPluginIndexes]
  );

  // Create the registry's context value and render
  const context = useMemo(
    () => ({ getPlugin, listPluginMetadata, defaultPluginKinds }),
    [getPlugin, listPluginMetadata, defaultPluginKinds]
  );
  return <PluginRegistryContext.Provider value={context}>{children}</PluginRegistryContext.Provider>;
}
